import { useAccountStore } from '@/stores/accountStore';

declare global {
	interface Window {
		analytics: any;
	}
}

type SegmentMethod = "track" | "identify" | "page" | "group" | "alias" | "reset";
type Event = {
	method: SegmentMethod;
	args: unknown[];
};

let eventQueue: Event[] = [];
let isAnalyticsReady = false;

const logger = {
	error: (message: string, error: Error) => {
		if (process.env.NODE_ENV !== 'production') {
			console.error(message, error);
		}
	}
};

function handleError(method: SegmentMethod, error: Error) {
	logger.error(`Error while tracking event with method: ${method}`, error);
}

let lastEventTime = Date.now();
const EVENT_RATE_LIMIT_MS = 100;

window.addEventListener('beforeunload', () => {
	flushQueue();
});

function flushQueue() {
	while (eventQueue.length && Date.now() - lastEventTime > EVENT_RATE_LIMIT_MS) {
		const { method, args } = eventQueue.shift()!;
		try {
			window.analytics[method](...args);
			lastEventTime = Date.now();
		} catch (error: Error | any) {
			handleError(method, error);
		}
	}

	if (eventQueue.length) {
		setTimeout(flushQueue, EVENT_RATE_LIMIT_MS);
	}
}

function trackOrQueue(method: SegmentMethod, args: unknown[]) {
	if (isAnalyticsReady) {
		eventQueue.push({ method, args });
		flushQueue();
	} else {
		eventQueue.push({ method, args });
	}
}

function loadSegment() {
	if (window.analytics) {
		window.analytics.ready(() => {
			isAnalyticsReady = true;
			flushQueue();
		});
	}
}

export default {
	getIdentity() {
		const accountStore = useAccountStore();
		const identity = {
			userId: accountStore.currentAccount.content?.userId,
			accountId: accountStore.currentAccount.content?.id
		};

		return identity;
	},
	track(eventName: string, properties?: any) {
		loadSegment();
		trackOrQueue('track', [eventName, this.getIdentity(), properties]);
	},
	identify() {
		loadSegment();
		trackOrQueue('identify', [this.getIdentity()]);
	},
	page(pageName: string, properties?: any) {
		loadSegment();
		const identity = this.getIdentity();

		if(identity.userId && identity.accountId) {
			trackOrQueue('page', [pageName, identity, properties]);
	}
	},
	reset() {
		if (isAnalyticsReady) {
			try {
				window.analytics.reset();
			} catch (error: Error | any) {
				handleError('reset', error);
			}
		}
	}
};
